import { Organization, OrganizationAccess } from '../api/organization';
import { SelfSortVariant } from '../components/SelfSort/selfSort.constants';

/**
 * The org creator = the earliest OrganizationAccess. The founder's access is
 * granted at org-creation time (before anyone else is invited), so it always has
 * the lowest id. Returns undefined while the accesses haven't loaded yet.
 *
 * There is no explicit owner/creator column — only Admin/Member roles — so this
 * earliest-access heuristic is how self-sort distinguishes the founder from other
 * admins (only the founder is asked for org firmographics, #2659).
 */
export function getOrgCreatorUserId(accesses: OrganizationAccess[] | undefined): number | undefined {
  if (!accesses?.length) return undefined;
  return accesses.reduce((earliest, a) => (a.id < earliest.id ? a : earliest)).userId;
}

/**
 * Pure variant resolver.
 *
 *   isOrgOwner + live org ──► variant
 *
 * Returns `undefined` until BOTH ownership and the org have resolved (the
 * data-ready gate — without it a brand-new creator can be misread before their
 * accesses load). "Owner" means the org CREATOR (not merely any admin), so a
 * non-founder admin gets the role-only flow and is never asked for the org's
 * firmographics (#2659). The creator only gets the full owner flow while the org
 * still has no firmographics; once collected, they too flip to the role-only flow.
 *
 * `companySize` MUST come from the live GET /organization, not the localforage
 * snapshot, which may not carry the column at all.
 */
export function resolveSelfSortVariant(
  isOrgOwner: boolean | undefined,
  organization: Organization | undefined
): SelfSortVariant | undefined {
  if (isOrgOwner === undefined || !organization) return undefined;
  const orgNeedsFirmographics = organization.companySize == null;
  return isOrgOwner && orgNeedsFirmographics ? 'owner' : 'member';
}

type UseSelfSortArgs = {
  isOrgOwner: boolean | undefined;
  organization: Organization | undefined;
  selfSortCompletedAt: string | null | undefined;
};

type UseSelfSortResult = {
  ready: boolean;
  isCompleted: boolean;
  variant: SelfSortVariant | undefined;
};

/**
 * Resolves the self-sort state that drives the sidebar banner. Pure logic — all
 * inputs injected so it can be unit-tested without fetching. The modal is opened
 * only on demand from the banner; it never auto-opens. `isCompleted` comes from
 * the JWT, so it persists across sessions/devices (server-backed).
 */
export function useSelfSort({ isOrgOwner, organization, selfSortCompletedAt }: UseSelfSortArgs): UseSelfSortResult {
  const isCompleted = selfSortCompletedAt != null;
  const ready = isOrgOwner !== undefined && !!organization;
  const variant = resolveSelfSortVariant(isOrgOwner, organization);

  return { ready, isCompleted, variant };
}
